import {
  DEFAULT_PER_RETURN_BILLING_FEE,
  PerReturnBillingSettings,
  Team,
} from "@redotech/redo-model/team";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { numberValidator } from "@redotech/ui/validators";
import { memo } from "react";

export const perReturnBillingSettingsForm = groupInput({
  enabled: input<boolean>(),
  usageFee: input<string>({ validator: numberValidator({ min: 0 }) }),
});

export type PerReturnBillingSettingsValue = InputSchema.Value<
  typeof perReturnBillingSettingsForm
>;

export const perReturnBillingSettingsDefault = {
  enabled: false,
  usageFee: `${DEFAULT_PER_RETURN_BILLING_FEE}`,
};

export type PerReturnBillingSettingsForm = InputSchema.Input<
  typeof perReturnBillingSettingsForm
>;

export function perReturnBillingSettingsFormValueToTeam(
  value: PerReturnBillingSettingsValue,
): PerReturnBillingSettings {
  return {
    enabled: value.enabled,
    usageFee: isNaN(Number(value.usageFee))
      ? DEFAULT_PER_RETURN_BILLING_FEE
      : Number(value.usageFee),
  };
}

export function perReturnBillingSettingsFormTeamToValue(
  team: Team,
): PerReturnBillingSettingsValue {
  return {
    enabled: team.settings.perReturnBilling?.enabled ?? false,
    usageFee: (
      team.settings.perReturnBilling?.usageFee ?? DEFAULT_PER_RETURN_BILLING_FEE
    ).toString(),
  };
}

export const PerReturnBillingSettingsCardV2 = memo(
  function PerReturnBillingSettingsCardV2({
    input,
  }: {
    input: PerReturnBillingSettingsForm;
  }) {
    return (
      <>
        <Flex align="center" dir="row" gap="md" justify="space-between">
          <Flex dir="column" gap="xs" grow="1">
            <Text fontSize="sm" textColor="tertiary">
              Charge merchants a fee for each return approved. This fee is
              applied when a return is approved by a merchant OR auto-approved.
            </Text>
          </Flex>
          <RedoToggle
            setValue={input.inputs.enabled.setValue}
            size="md"
            value={input.inputs.enabled.value}
          />
        </Flex>

        <Flex dir="column" gap="md">
          <RedoTextInput
            IconTrailing={() => <span>USD</span>}
            label="Usage Fee (per return)"
            min={0}
            placeholder="0.00"
            setValue={input.inputs.usageFee.setValue}
            state={!input.inputs.enabled.value ? "disabled" : "default"}
            step={0.01}
            value={input.inputs.usageFee.value}
          />
          {input.inputs.usageFee.errors.length > 0 && (
            <Text fontSize="xs" textColor="error">
              {input.inputs.usageFee.errors[0]}
            </Text>
          )}
        </Flex>
      </>
    );
  },
);
